import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Building2, X, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { useCompanies, type CompanyFilters } from "@/hooks/queries/useCompanies";
import { useFollowedCompanies } from "@/hooks/queries/useFollowedCompanies";
import {
  useToggleFollowCompany,
  useUnlockCompany,
} from "@/hooks/mutations/useCompanyMutations";
import { formatNumber, formatMoneyCompact } from "@/lib/format";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Paginator } from "@/components/common/Paginator";
import { FilterBar, type FilterValues } from "@/components/filters/FilterBar";
import { ChoicePill } from "@/components/filters/ChoicePill";
import { ChipListModal } from "@/components/common/ChipListModal";
import { CompaniesSidebar } from "@/components/companies/CompaniesSidebar";
import { CompanyKpis, type CompanyKpi } from "@/components/companies/CompanyKpis";
import {
  COMPANY_GRID_COLS,
  CompanyResultRow,
} from "@/components/companies/CompanyResultRow";
import type { CompanyItem } from "@/types/domain";

const EMPTY_FILTERS: FilterValues = {
  departmentIds: [],
  provinceIds: [],
  districtIds: [],
  dateFrom: "",
  dateTo: "",
  entityIds: [],
  subjects: [],
  stages: [],
  maxDays: null,
  onlyInterests: false,
};

const STORAGE_KEY = "licitigo_companies_filters";

const AMOUNT_OPTIONS = [
  { value: null, label: "Todos" },
  { value: 1_000_000, label: "Más de S/ 1M" },
  { value: 10_000_000, label: "Más de S/ 10M" },
  { value: 100_000_000, label: "Más de S/ 100M" },
];

const ACTIVITY_OPTIONS = [
  { value: null, label: "Todo el tiempo" },
  { value: 3, label: "Últimos 3 meses" },
  { value: 6, label: "Últimos 6 meses" },
  { value: 12, label: "Últimos 12 meses" },
];

type CompanySort = NonNullable<CompanyFilters["sortBy"]>;

const SORT_OPTIONS: { value: CompanySort; label: string }[] = [
  { value: "resources", label: "Monto de recursos" },
  { value: "awarded", label: "Monto adjudicado" },
];

type PersistedCompanyFilters = {
  keywords: string[];
  companyTerms: string[];
  filters: FilterValues;
  minAmount: number | null;
  activeSinceMonths: number | null;
  sortBy: CompanySort;
};

function loadPersistedFilters(): Partial<PersistedCompanyFilters> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<PersistedCompanyFilters>) : {};
  } catch {
    return {};
  }
}

export default function Companies() {
  const navigate = useNavigate();
  const persisted = useMemo(loadPersistedFilters, []);
  const [keywords, setKeywords] = useState<string[]>(persisted.keywords ?? []);
  const [companyTerms, setCompanyTerms] = useState<string[]>(
    persisted.companyTerms ?? [],
  );
  const [filters, setFilters] = useState<FilterValues>(
    persisted.filters
      ? { ...EMPTY_FILTERS, ...persisted.filters }
      : EMPTY_FILTERS,
  );
  const [minAmount, setMinAmount] = useState<number | null>(persisted.minAmount ?? null);
  const [activeSinceMonths, setActiveSinceMonths] = useState<number | null>(
    persisted.activeSinceMonths ?? null,
  );
  const [sortBy, setSortBy] = useState<CompanySort>(
    persisted.sortBy ?? "resources",
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [palabrasOpen, setPalabrasOpen] = useState(false);
  const [empresasOpen, setEmpresasOpen] = useState(false);
  const followed = useFollowedCompanies();
  const unlockCompany = useUnlockCompany();
  const toggleFollow = useToggleFollowCompany();

  // Persist the company filters across reloads (mirrors the legacy Angular page).
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          keywords,
          companyTerms,
          filters,
          minAmount,
          activeSinceMonths,
          sortBy,
        } satisfies PersistedCompanyFilters),
      );
    } catch {
      // Sin almacenamiento los filtros simplemente no se recuerdan.
    }
  }, [keywords, companyTerms, filters, minAmount, activeSinceMonths, sortBy]);

  const { data, isLoading, isFetching } = useCompanies(
    {
      keywords: keywords.length ? keywords : undefined,
      searchTerms: companyTerms.length ? companyTerms : undefined,
      departmentIds: filters.departmentIds.length
        ? filters.departmentIds
        : undefined,
      subjects: filters.subjects.length ? filters.subjects : undefined,
      dateFrom: filters.dateFrom || null,
      dateTo: filters.dateTo || null,
      minAmount,
      activeSinceMonths,
      sortBy,
    },
    page,
    pageSize,
  );

  // El backend ya devuelve la página ordenada por monto adjudicado.
  const pageItems = data?.companies ?? [];
  const total = data?.total ?? 0;

  // keepPreviousData deja la data vieja visible en los refetch, así que isLoading
  // solo cubre la primera carga; isFetching es la señal de "recargando resultados".
  const isUpdating = isFetching && !isLoading;

  const summary = data?.summary ?? null;
  const kpis: CompanyKpi[] = [
    {
      label: "Empresas encontradas",
      value: formatNumber(total),
      caption: keywords.length ? `Compran ${keywords.join(", ")}` : "Con contratos adjudicados",
      valueClass: "text-blue-600 dark:text-blue-400",
    },
    {
      label: "Monto en recursos",
      value: summary ? formatMoneyCompact(summary.resourcesAmount) : "—",
      caption: summary ? "Suma de ítems coincidentes" : "Añade un recurso para verlo",
      valueClass: "text-emerald-600 dark:text-emerald-400",
    },
    {
      label: "Ítems coincidentes",
      value: summary ? formatNumber(summary.itemsCount) : "—",
      caption: summary
        ? `En ${formatNumber(summary.worksCount)} obras adjudicadas`
        : "Añade un recurso para verlo",
      valueClass: "text-orange-500 dark:text-orange-400",
    },
    {
      label: "Siguiendo",
      value: formatNumber(followed.companies.length),
      caption: `${followed.newAwardsThisWeek} nuevas adjudicaciones esta semana`,
      valueClass: "text-purple-600 dark:text-purple-400",
    },
  ];

  return (
    <div className="mx-auto w-full flex-1 px-4 py-6 md:px-14">
      <div className="mb-6">
        <p className="text-sm font-bold uppercase tracking-wide text-purple-600">
          Inteligencia de proveedores
        </p>
        <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight">
          Empresas
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Filtra por{" "}
          <span className="font-semibold text-foreground/80">recursos</span> y
          descubre qué proveedores compran lo que vendes.{" "}
          <span className="font-semibold text-foreground/80">Síguelas</span> para
          recibir un aviso cada vez que ganen una nueva licitación.
        </p>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
        <aside className="order-last lg:order-first lg:w-72 lg:shrink-0">
          <CompaniesSidebar />
        </aside>

        <div className="flex min-w-0 flex-1 flex-col gap-4 lg:gap-6">
          {/* Filters card */}
          <div className="rounded-2xl bg-card p-6 shadow-sm">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:divide-x">
              <SearchTermGroup
                label="Recursos"
                description={
                  <>
                    Materiales, equipos y subcontratos que las empresas compran{" "}
                    <span className="font-semibold text-foreground/80">
                      dentro del presupuesto de sus obras
                    </span>
                    .
                  </>
                }
                terms={keywords}
                onEdit={() => setPalabrasOpen(true)}
                onRemove={(kw) => {
                  setKeywords((l) => l.filter((x) => x !== kw));
                  setPage(0);
                }}
                className="md:pr-6"
              />
              <SearchTermGroup
                label="Empresas"
                description="Busca por RUC o razón social para comparar competidores concretos."
                terms={companyTerms}
                onEdit={() => setEmpresasOpen(true)}
                onRemove={(term) => {
                  setCompanyTerms((l) => l.filter((x) => x !== term));
                  setPage(0);
                }}
                className="md:pl-6"
              />
            </div>

            <Separator className="my-5" />

            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <FilterBar
                  value={filters}
                  onChange={(patch) => {
                    setFilters((f) => ({ ...f, ...patch }));
                    setPage(0);
                  }}
                  showEntity={false}
                  showStages={false}
                  showContratado={false}
                  showOnlyInterests={false}
                />
              </div>
              <ChoicePill
                label="Monto"
                options={AMOUNT_OPTIONS}
                value={minAmount}
                defaultValue={null}
                onChange={(value) => {
                  setMinAmount(value);
                  setPage(0);
                }}
              />
              <ChoicePill
                label="Actividad"
                options={ACTIVITY_OPTIONS}
                value={activeSinceMonths}
                defaultValue={null}
                onChange={(value) => {
                  setActiveSinceMonths(value);
                  setPage(0);
                }}
              />
              {/* Sin recursos buscados no hay monto de recursos: el backend ordena por lo adjudicado. */}
              {keywords.length > 0 && (
                <ChoicePill<CompanySort>
                  label="Ordenar por"
                  options={SORT_OPTIONS}
                  value={sortBy}
                  defaultValue="resources"
                  onChange={(value) => {
                    setSortBy(value);
                    setPage(0);
                  }}
                />
              )}
            </div>
          </div>

          <CompanyKpis kpis={kpis} />

          {/* Resultados */}
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold tracking-tight">Resultados</h2>
              {isUpdating && (
                <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" />
                  Actualizando…
                </span>
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              Mostrando {pageItems.length} de {formatNumber(total)} empresas
            </span>
          </div>

          <div className="overflow-hidden rounded-2xl bg-card shadow-sm">
            {isLoading ? (
              <div className="space-y-2 p-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-lg" />
                ))}
              </div>
            ) : (
              <div
                className={`overflow-x-auto transition-opacity ${
                  isUpdating ? "pointer-events-none opacity-50" : ""
                }`}
              >
                <div
                  className="grid min-w-[900px] items-center gap-4 border-b bg-slate-50 px-6 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:bg-slate-900 dark:text-slate-400"
                  style={{ gridTemplateColumns: COMPANY_GRID_COLS }}
                >
                  <div>Empresa</div>
                  <div>Consorcios</div>
                  <div className="text-center">Contratos</div>
                  <div>Última adj.</div>
                  <div className="text-right text-emerald-600 dark:text-emerald-400">
                    Monto · Recursos
                  </div>
                  <div />
                </div>

                {pageItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
                    <Building2 className="size-10 text-muted-foreground/30" />
                    <p className="font-semibold">No se encontraron empresas</p>
                    <p className="max-w-sm text-sm text-muted-foreground">
                      Prueba ajustando los recursos o los filtros de
                      departamento, objeto o rango de fechas.
                    </p>
                  </div>
                ) : (
                  <div className="min-w-[900px] divide-y">
                    {pageItems.map((c: CompanyItem, index) => (
                      <CompanyResultRow
                        key={c.company_id}
                        company={c}
                        following={followed.isFollowing(c.company_id)}
                        newAwards={followed.newAwardsOf(c.company_id)}
                        defaultExpanded={index === 0}
                        unlocking={
                          unlockCompany.isPending &&
                          unlockCompany.variables?.companyId === c.company_id
                        }
                        onUnlock={() => unlockCompany.mutate({ companyId: c.company_id })}
                        onToggleFollow={() =>
                          toggleFollow.mutate({
                            companyId: c.company_id,
                            follow: !followed.isFollowing(c.company_id),
                          })
                        }
                        onOpen={() => navigate(`/companies/${c.company_id}`)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            <Paginator
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setPage(0);
              }}
              pageSizeOptions={[10, 25, 50]}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      <ChipListModal
        open={palabrasOpen}
        onOpenChange={setPalabrasOpen}
        title="Gestionar recursos"
        description='Escribe lo que vendes (ej. "cemento", "andamio", "fierro corrugado"). Buscamos las empresas que lo compraron dentro del presupuesto de sus obras.'
        inputLabel="Recurso*"
        listLabel="Tus recursos:"
        emptyLabel="No has agregado ningún recurso todavía. Usa el formulario de arriba para agregarlos."
        items={keywords}
        onSave={(v) => {
          setKeywords(v);
          setPage(0);
        }}
      />
      <ChipListModal
        open={empresasOpen}
        onOpenChange={setEmpresasOpen}
        title="Gestionar búsqueda de Empresas"
        theme="emerald"
        description='Ingresa nombres, palabras clave o números de RUC de las empresas que deseas buscar (ej. "CLG", "DAMBEZ", "20601234567"). El sistema filtrará los proveedores que contengan alguno de estos términos en su nombre o número de RUC.'
        inputLabel="Nombre o RUC de Empresa*"
        listLabel="Tus filtros de empresas:"
        emptyLabel="No has agregado ningún filtro de empresa todavía. Usa el formulario de arriba para agregar términos."
        items={companyTerms}
        onSave={(v) => {
          setCompanyTerms(v);
          setPage(0);
        }}
      />
    </div>
  );
}

/** Una de las dos búsquedas de la tarjeta de filtros: recursos o empresas. */
function SearchTermGroup({
  label,
  description,
  terms,
  onEdit,
  onRemove,
  className,
}: {
  label: string;
  description: React.ReactNode;
  terms: string[];
  onEdit: () => void;
  onRemove: (term: string) => void;
  className?: string;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-2", className)}>
      <div className="flex items-center gap-2">
        <span className="size-2 rounded-full bg-blue-600 dark:bg-blue-500" />
        <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-700 dark:text-slate-200">
          {label}
        </span>
        {terms.length > 0 && (
          <span className="rounded-full bg-slate-100 px-1.5 text-[10px] font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            {terms.length}
          </span>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{description}</p>
      <div className="flex flex-wrap items-center gap-2">
        {terms.map((term) => (
          <span
            key={term}
            onClick={onEdit}
            className="flex cursor-pointer items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-bold uppercase text-blue-700 transition-colors hover:border-blue-300 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300"
          >
            {term}
            <button
              type="button"
              aria-label={`Quitar ${term}`}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(term);
              }}
              className="flex size-4 cursor-pointer items-center justify-center rounded-full text-blue-400 hover:text-blue-700 dark:hover:text-blue-200"
            >
              <X className="size-3.5" />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={onEdit}
          className="flex cursor-pointer items-center gap-1 rounded-full border border-dashed px-3.5 py-1.5 text-xs font-bold text-muted-foreground transition-colors hover:bg-accent"
        >
          + Añadir
        </button>
      </div>
    </div>
  );
}
